import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { DirtyFormRegistryService } from '../services/dirty-form-registry.service';

/** Components that guard navigation away from themselves implement this. */
export interface CanComponentDeactivate {
    canDeactivate: () => boolean | Promise<boolean>;
}

/**
 * Composed CanDeactivate guard. First asks the DirtyFormRegistryService to confirm every dirty leaf
 * currently mounted anywhere under this route (form pages, and modals nested at any depth) — this is
 * what catches Back/sidebar/breadcrumb navigation bypassing an unsaved-changes prompt regardless of
 * how deeply the dirty component sits in the tree. Then falls back to the routed component's own
 * canDeactivate(), for page-scoped guards like DocumentsPage's mid-upload check — but only when that
 * component wasn't already asked by the registry loop above. A directly-routed form (e.g. ExpenseForm)
 * is both the routed component AND self-registered, so without this check it would be asked twice per
 * navigation attempt: the second call re-shows the leave-confirmation modal after the first one already
 * resolved it, orphaning a promise that never settles and silently wedging all future navigation.
 */
export const canDeactivateGuard: CanDeactivateFn<CanComponentDeactivate> = async (component) => {
    const registry = inject(DirtyFormRegistryService);
    if (!(await registry.confirmAllCanDeactivate())) {
        return false;
    }
    if (component && registry.isRegistered(component)) {
        return true;
    }
    return component?.canDeactivate ? component.canDeactivate() : true;
};
